import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { apiClient } from "~/api/ApiClient";
import { Icon } from "~/assets/icons/Icon";
import Button from "~/components/mantine/button/Button";
import Modal from "~/components/ui/modal/Modal";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";

import { GoogleAccountHandle } from "../GoogleAccountHandle";
import { UserIdentityWithAuthorizations } from "../withAuthorizations";
import styles from "./ConnectedAccountRow.module.scss";

export const ConnectedAccountRow = ({
  identity,
  siteId,
}: {
  identity: UserIdentityWithAuthorizations;
  siteId: number;
}) => {
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const { mutateAsync: deleteUserIdentity, isPending: isDeleting } =
    useMutation({
      mutationFn: async (id: number) => {
        return await apiClient.userIdentities.userIdentitiesDelete(id);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.USER_IDENTITIES,
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
        });
      },
    });

  const onDisconnect = async (id: number) => {
    const result = await tryCatch(deleteUserIdentity(id));

    if (result.error) {
      enqueueSnackbar(messageFromError(result.error), {
        variant: "error",
        persist: true,
      });

      return;
    }

    enqueueSnackbar("Google analytics account disconnected", {
      variant: "success",
    });
  };

  return (
    <tr>
      <td>
        <GoogleAccountHandle identity={identity.record} variant="full" />
      </td>
      <td>
        {identity.can.destroy && (
          <div className={styles.disconnectButtonWrapper}>
            <Button
              onClick={() => setIsDisconnectModalOpen(true)}
              loading={isDeleting}
              variant="nohoverWithPadding"
            >
              <Icon iconName={"RedCrossmarkIcon"} width="1rem" height="1rem" />
            </Button>
            <Modal
              isOpen={isDisconnectModalOpen}
              modalLabel={
                <h3>Are you sure you want to disconnect this account?</h3>
              }
              closeModal={() => setIsDisconnectModalOpen(false)}
            >
              <GoogleAccountHandle identity={identity.record} variant="full" />
              <div className={styles.modalActions}>
                <Button
                  variant="outlined"
                  onClick={() => setIsDisconnectModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    onDisconnect(identity.record.id);
                    setIsDisconnectModalOpen(false);
                  }}
                >
                  Disconnect
                </Button>
              </div>
            </Modal>
          </div>
        )}
      </td>
    </tr>
  );
};
